import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const RegionContext = createContext(null);

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) throw new Error('useRegion must be used within a RegionProvider');
  return context;
};

// Static region configs — no toggle, driven entirely by user.market
const REGION_CONFIG = {
  US: {
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    dateFormat: 'MM/DD/YYYY',
    benchmarks: [
      { id: 'SP500', label: 'S&P 500' },
      { id: 'NASDAQ', label: 'NASDAQ' },
      { id: 'BERKSHIRE', label: 'Berkshire Hathaway' },
    ],
    taxConfig: {
      sectionLabel: 'Capital Gains',
      shortTermLabel: 'Short-Term (< 1 year)',
      longTermLabel: 'Long-Term (≥ 1 year)',
      shortTermNote: 'Taxed as ordinary income',
      longTermNote: '0% / 15% / 20% depending on income bracket',
      filingForms: ['Schedule D', '1099-B', 'K-1'],
      currency: 'USD',
    },
    assetClasses: [
      'STOCKS', 'ETF', 'MUTUAL_FUNDS', 'BONDS', 'F_AND_O',
      'ALTERNATIVES', 'REAL_ESTATE', 'ART', 'GOLD',
    ],
    hiddenAssetClasses: ['NPS', 'PPF_EPF', 'PMS', 'AIF'],
    marketOutlookPrimary: 'us',
  },
  IN: {
    currency: 'INR',
    currencySymbol: '₹',
    locale: 'en-IN',
    dateFormat: 'DD/MM/YYYY',
    benchmarks: [
      { id: 'NIFTY50', label: 'NIFTY 50' },
      { id: 'SENSEX', label: 'SENSEX' },
      { id: 'NIFTY_MIDCAP', label: 'NIFTY Midcap 150' },
      { id: 'NIFTY_SMALLCAP', label: 'NIFTY Smallcap 250' },
    ],
    taxConfig: {
      sectionLabel: 'Capital Gains',
      shortTermLabel: 'Short-Term Capital Gains (STCG)',
      longTermLabel: 'Long-Term Capital Gains (LTCG)',
      shortTermNote: 'Listed equity: 15% | Debt/others: slab rate',
      longTermNote: 'Listed equity: 10% above ₹1L | Debt: 20% with indexation',
      filingForms: ['ITR-2', 'ITR-3', 'Schedule CG'],
      currency: 'INR',
    },
    assetClasses: [
      'STOCKS', 'MUTUAL_FUNDS', 'F_AND_O', 'BONDS', 'ETF',
      'FD', 'NPS', 'PPF_EPF', 'PMS', 'AIF',
      'GOLD', 'REAL_ESTATE', 'ART', 'ALTERNATIVES',
    ],
    hiddenAssetClasses: [],
    marketOutlookPrimary: 'india',
  },
};

export const RegionProvider = ({ children }) => {
  const { user } = useAuth();

  const region = useMemo(() => {
    const market = user?.market || 'IN';
    const config = REGION_CONFIG[market] || REGION_CONFIG.IN;
    return {
      market,
      isUS: market === 'US',
      isIndia: market === 'IN',
      ...config,
    };
  }, [user?.market]);

  return (
    <RegionContext.Provider value={region}>
      {children}
    </RegionContext.Provider>
  );
};
